import { Container, Button } from '../ui';
import { Linkedin, X, Youtube } from 'lucide-react';

/**
 * Simple Reddit icon component (lucide-react doesn't have Reddit icon)
 */
function RedditIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
    );
}

/**
 * Simple Medium icon component
 */
function MediumIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42a3.42 3.42 0 01-3.37-6.42c0-3.54 1.5-6.42 3.37-6.42s3.38 2.88 3.38 6.42zM24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.2-2.58-1.2-5.75s.54-5.75 1.2-5.75c.66 0 1.19 2.58 1.19 5.75z" />
        </svg>
    );
}

/**
 * Footer component with site links and contact button.
 */
export function Footer() {
    const year = new Date().getFullYear();

    return (
        <footer className="backdrop-blur-[10px] saturate-[1.25] bg-black/20 text-slate-300 border-t border-white/10 mt-36">
            <Container className="flex flex-col lg:flex-row gap-12 py-12">
                <div className="lg:w-1/4 flex flex-col gap-4">
                    <div>
                        <div className="font-bold text-white mb-1">MCP Cloud</div>
                        <div className="text-sm opacity-60">© {year} • All rights reserved.</div>
                    </div>
                    <div className="flex flex-col gap-4 items-start">
                        <Button variant="outline" href="/contact" size="sm">
                            Contact
                        </Button>
                        <div className="flex items-center gap-3">
                            <a
                                href="https://www.linkedin.com/company/mcp-cloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-white transition-all duration-300 hover:scale-110"
                                aria-label="LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </a>
                            <a
                                href="https://x.com/McpCloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-white transition-all duration-300 hover:scale-110"
                                aria-label="X (Twitter)"
                            >
                                <X className="w-5 h-5" />
                            </a>
                            <a
                                href="https://www.reddit.com/r/MCP_Cloud/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-white transition-all duration-300 hover:scale-110"
                                aria-label="Reddit"
                            >
                                <RedditIcon className="w-5 h-5" />
                            </a>
                            <a
                                href="https://medium.com/@info_51237"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-white transition-all duration-300 hover:scale-110"
                                aria-label="Medium"
                            >
                                <MediumIcon className="w-5 h-5" />
                            </a>
                            <a
                                href="https://www.youtube.com/@MCP-Cloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-white transition-all duration-300 hover:scale-110"
                                aria-label="YouTube"
                            >
                                <Youtube className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </div>
                <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-8">
                    <div>
                        <h4 className="font-semibold text-white mb-4">Product</h4>
                        <ul className="space-y-2 text-sm">
                            <li><a href="/about" className="text-slate-400 hover:text-white transition-all duration-300">About</a></li>
                            <li><a href="/features" className="text-slate-400 hover:text-white transition-all duration-300">Features</a></li>
                            <li><a href="/why-mcp-cloud" className="text-slate-400 hover:text-white transition-all duration-300">Why MCP Cloud</a></li>
                            <li><a href="/pricing" className="text-slate-400 hover:text-white transition-all duration-300">Pricing</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white mb-4">Company</h4>
                        <ul className="space-y-2 text-sm">
                            <li><a href="/company" className="text-slate-400 hover:text-white transition-all duration-300">About</a></li>
                            <li><a href="/services" className="text-slate-400 hover:text-white transition-all duration-300">Services</a></li>
                            <li><a href="/careers" className="text-slate-400 hover:text-white transition-all duration-300">Career</a></li>
                            <li><a href="/contact" className="text-slate-400 hover:text-white transition-all duration-300">Contact</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white mb-4">Resources</h4>
                        <ul className="space-y-2 text-sm">
                            <li><a href="/blog" className="text-slate-400 hover:text-white transition-all duration-300">Blog</a></li>
                            <li><a href="/faq" className="text-slate-400 hover:text-white transition-all duration-300">FAQ</a></li>
                            <li><a href="https://modelcontextprotocol.io/docs/getting-started/intro" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-all duration-300">MCP Docs</a></li>
                            <li><a href="https://registry.modelcontextprotocol.io/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-all duration-300">Official MCP Registry</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white mb-4">Legal</h4>
                        <ul className="space-y-2 text-sm">
                            <li><a href="/privacy" className="text-slate-400 hover:text-white transition-all duration-300">Privacy</a></li>
                            <li><a href="/terms" className="text-slate-400 hover:text-white transition-all duration-300">Terms</a></li>
                            <li><a href="/cookies" className="text-slate-400 hover:text-white transition-all duration-300">Cookies</a></li>
                            <li><a href="/disclaimer" className="text-slate-400 hover:text-white transition-all duration-300">Disclaimer</a></li>
                        </ul>
                    </div>
                </div>
            </Container>
        </footer>
    );
}
